import { StatusBar, Style } from '@capacitor/status-bar';
import React, { Suspense, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import LoginForm from './components/forms/LoginForm';
import RegisterForm from './components/forms/RegisterForm';
import AuthWrapper from './components/wrappers/AuthWrapper';
import RouteWrapper from './components/wrappers/RouteWrapper';
import usePushNotification from './hooks/UsePushNotfication';
import useUser from './hooks/UseUser';
import { addFirebaseDevice } from './services/FirebaseMessaging';
import ForgotPasswordForm from 'components/forms/ForgotPasswordForm';
import SpinnerFullPage from 'components/spinner/SpinnerFullPage';
import { SplashScreen } from '@capacitor/splash-screen';
import { IS_MOBILE_APP } from 'const';
import OuterWrapper from 'components/wrappers/OuterWrapper';
import { Theme } from 'context/UserContext';
import Loading from 'components/loading';
import * as Sentry from '@sentry/react';
import PageError from 'components/PageError';
import useAppStateChange from 'hooks/UseAppStateChange';

const Groups = React.lazy(() => import('pages/Groups'));
const Home = React.lazy(() => import('pages/Home'));
const Ranked = React.lazy(() => import('pages/Ranked'));
const Starred = React.lazy(() => import('pages/Starred'));
const NotFound = React.lazy(() => import('pages/NotFound'));
const Profile = React.lazy(() => import('pages/Profile'));
const LeaderboardSettingsPage = React.lazy(() => import('pages/LeaderboardSettingsPage'));
const NotificationSettingsPage = React.lazy(() => import('pages/NotificationSettingsPage'));
const DeleteProfile = React.lazy(() => import('pages/DeleteProfile'));
const Devices = React.lazy(() => import('pages/Devices'));
const ModulePage = React.lazy(() => import('pages/ModulePage'));
const Settings = React.lazy(() => import('pages/Settings'));
const GroupDetail = React.lazy(() => import('pages/GroupDetail'));

export default function App(): JSX.Element {
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [initialLoaded, setInitialLoaded] = useState<boolean>(false);
  const [pushNotificationToken, setPushNotificationToken] = useState<string>();

  const { user, loadUser, loading } = useUser();
  const { enablePushNotification, onPushNotificationRegistered } = usePushNotification();

  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
  const { appStateChange } = useAppStateChange();

  /**
   * Check if the app is active after an hour, if so; reload the whole page
   */
  useEffect(() => {
    if (IS_MOBILE_APP) {
      const abortController = new AbortController();
      appStateChange(active => {
        if (abortController.signal.aborted) return;
        if (active === false) {
          localStorage.setItem('lastActive', new Date().toISOString());
        } else {
          const lastActive = localStorage.getItem('lastActive');
          if (lastActive) {
            const lastActiveDate = new Date(lastActive);
            const now = new Date();
            const diff = now.getTime() - lastActiveDate.getTime();
            // refresh the page if needed
            const hour = 1000 * 60 * 60;
            if (diff > hour) {
              console.log('reloading the page');
              window.location.reload();
            }
          }
        }
      }, abortController.signal);

      return () => abortController.abort();
    }
  }, [appStateChange]);

  /**
   * Start the push notifications
   */
  useEffect(() => {
    // listen when we registered a push notification and on success, set the token
    // this token will be saved in the API/BE (after the user object has been defined)
    onPushNotificationRegistered(token => setPushNotificationToken(token));
    // enable push notifications
    enablePushNotification();
  }, []); //eslint-disable-line

  /**
   * Save the Push Notification token to the server, so we can send notifications
   */
  useEffect(() => {
    if (user && pushNotificationToken) {
      addFirebaseDevice(pushNotificationToken).catch(e => console.error(e));
    }
  }, [user, pushNotificationToken]);

  /**
   * Fetch the user data at first
   * - hit an error? Then show the login
   * - if fetched the user, go further and show the data
   */
  useEffect(() => {
    const abortController = new AbortController();
    loadUser(abortController.signal)
      .then(() => setInitialLoaded(true))
      .catch(() => {
        if (!abortController.signal.aborted) {
          setShowLogin(true);
          setInitialLoaded(true);
        }
      });

    return () => abortController.abort();
  }, [loadUser, setShowLogin, setInitialLoaded]);

  /**
   * If the user object got changed by the logout (or...)
   * show the login so the user can login again.
   */
  useEffect(() => {
    if (!initialLoaded) return;
    if (user) {
      setShowLogin(false);
    } else {
      setShowLogin(true);
    }
  }, [initialLoaded, loading, user]);

  /**
   * Remove the splashscreen for the mobile APP
   */
  useEffect(() => {
    if (IS_MOBILE_APP) {
      SplashScreen.hide();
    }
  }, []);

  /**
   * Set the theme based on the user settings or system settings
   */
  useEffect(() => {
    // detect the dark mode
    const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const htmlTag = document.getElementsByTagName('html');
    if (!htmlTag) return;

    let setDark = false;

    // if we have a user, set the theme based on the user settings
    if (user) {
      // set the htmlClassname based on the user theme
      if (user.theme === Theme.Dark) {
        setDark = true;
      } else if (user.theme === Theme.Auto && isDarkMode) {
        setDark = true;
      }
    } else if (isDarkMode) {
      // if we don't have a user, set the theme based on the system settings
      setDark = true;
    }

    if (setDark) {
      htmlTag[0].classList.add('dark');
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    } else {
      htmlTag[0].classList.remove('dark');
      StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
  }, [user]);

  // still loading
  if (!initialLoaded) {
    return <SpinnerFullPage />;
  }

  /**
   * show the login form in needed
   */
  if (showLogin && !loading) {
    return (
      <Sentry.ErrorBoundary fallback={<PageError />}>
        <OuterWrapper>
          <SentryRoutes>
            <Route path='/' element={<AuthWrapper />}>
              <Route
                path=''
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <LoginForm />
                  </Suspense>
                }
              />
              <Route
                path='signup'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <RegisterForm />
                  </Suspense>
                }
              />
              <Route
                path='forgot'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <ForgotPasswordForm />
                  </Suspense>
                }
              />
              <Route
                path='*'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <LoginForm />
                  </Suspense>
                }
              />
            </Route>
          </SentryRoutes>
        </OuterWrapper>
      </Sentry.ErrorBoundary>
    );
  }

  /**
   * Show the app
   */
  if (initialLoaded) {
    return (
      <Sentry.ErrorBoundary fallback={<PageError />}>
        <OuterWrapper>
          <SentryRoutes>
            <Route element={<RouteWrapper />}>
              <Route
                path='/'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <Home />
                  </Suspense>
                }
              />
              <Route
                path='/ranked'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <Ranked />
                  </Suspense>
                }
              />
              <Route
                path='/starred'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <Starred />
                  </Suspense>
                }
              />
              <Route
                path='/devices'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <Devices />
                  </Suspense>
                }
              />
              <Route
                path='/settings'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <Settings />
                  </Suspense>
                }
              />
              <Route
                path='/settings/profile'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <Profile />
                  </Suspense>
                }
              />
              <Route
                path='/settings/profile/delete'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <DeleteProfile />
                  </Suspense>
                }
              />
              <Route
                path='/settings/module'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <ModulePage />
                  </Suspense>
                }
              />
              <Route
                path='/settings/leaderboard'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <LeaderboardSettingsPage />
                  </Suspense>
                }
              />
              <Route
                path='/settings/notifications'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <NotificationSettingsPage />
                  </Suspense>
                }
              />
              <Route
                path='/groups'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <Groups />
                  </Suspense>
                }
              />
              <Route
                path='/group/:groupId'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <GroupDetail />
                  </Suspense>
                }
              />
              <Route
                path='*'
                element={
                  <Suspense fallback={<Loading isFixed={false} initialWithoutFade={true} visible={true} loadingText='...loading' />}>
                    <NotFound />
                  </Suspense>
                }
              />
            </Route>
          </SentryRoutes>
        </OuterWrapper>
      </Sentry.ErrorBoundary>
    );
  }

  return <></>;
}
